import React, { useEffect, useState } from 'react'
import './bpCount.css'
import { Button } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { utils, writeFile } from 'xlsx-js-style'
import swal from 'sweetalert'
import { ButtonList } from 'sweetalert/typings/modules/options/buttons'
import CustomDatePicker from '../../../widgets/datePicker/datePicker'
import AgGridContainer from '../../../componant/gridContainer/gridContainer'
import { SnackbarService } from '../../../services/snackBehaviorSubject/snackBehaviorSubject'
import { calculateIncentive, editBPCount, getCalculatedIncentive, lockIncentiveDateRange } from '../../../api-services/employee-services'

interface DateRangeProps {
  startDate: string,
  endDate: string
}

interface BPCountRowProps {
  employeeId: string,
  employeeName: string,
  date: string,
  count: number,
  originalCount: number,
  incentive: number
}

interface EditedBPCountRow {
  employeeId: string,
  date: string,
  count: number
}

const dateRangeField: { name: keyof DateRangeProps, label: string }[] = [
  { name: 'startDate', label: 'From Date *' },
  { name: 'endDate', label: 'To Date *' }
]

const initialDateRange: DateRangeProps = {
  startDate: '',
  endDate: ''
}

const BPCount = () => {
  const snackbarService = SnackbarService()
  const [selectedDate, setSelectedDate] = useState<DateRangeProps>(initialDateRange)
  const [lockedDateRange, setLockedDateRange] = useState<DateRangeProps>(initialDateRange)
  const [errors, setErrors] = useState<DateRangeProps>(initialDateRange)
  const [rowData, setRowData] = useState<BPCountRowProps[]>([])
  const [editedRows, setEditedRows] = useState<Record<string, EditedBPCountRow>>({})


  const fetchIncentive = () => {
    getCalculatedIncentive().then((response: any) => {
      if (response?.status === 200) {
        setRowData(response.data.reverse())
      }
    })
  }

  useEffect(() => {
    fetchIncentive()
  }, [])

  const [columnDefs] = useState([
    {
      headerName: 'Date',
      field: 'date',
      minWidth: 130,
      flex: 1,
      resizable: true,
      filter: true,
      valueFormatter: (params: any) => {
        if (!params.value) return '';

        const [date, time] = params.value.split('T');
        const [year, month, day] = date.split('-');

        return `${day}/${month}/${year} ${time?.substring(0, 8)}`;
      }
    },
    {
      headerName: 'User Id',
      field: 'employeeId',
      minWidth: 130,
      flex: 1,
      resizable: true,
      filter: true,
    },
    {
      headerName: 'Name',
      field: 'employeeName',
      minWidth: 160,
      flex: 1,
      resizable: true,
      filter: true,
    },
    {
      headerName: 'Operator Count',
      field: 'count',
      minWidth: 150,
      flex: 1,
      resizable: true,
      filter: true,
      editable: true,
    },
    {
      headerName: 'Original Count',
      field: 'originalCount',
      minWidth: 150,
      flex: 1,
      resizable: true,
      filter: true,
    },
    {
      headerName: 'Incentive Amount',
      field: 'incentive',
      minWidth: 160,
      flex: 1,
      resizable: true,
      filter: true,
      valueFormatter: (params: any) => {
        if (params.value === undefined || params.value === null) {
          return '';
        }
        return `₹ ${params.value.toLocaleString('en-IN')}`;
      }
    }
  ])

  const onCellValueChanged = (e: any) => {
    setRowData((prev) => prev.map((row) => row.employeeId === e.data.employeeId && row.date === e.data.date ? e.data : row))
    const key = `${e.data.employeeId}_${e.data.date}`
    setEditedRows((prev) => ({
      ...prev,
      [key]: { employeeId: e.data.employeeId, date: e.data.date?.split('T')[0], count: e.data.count }
    }))
  }

  const handleDate = (date: string, name: string) => {
    setSelectedDate((prev) => {
      const updated = { ...prev, [name]: date }
      if (updated.startDate && updated.endDate && new Date(updated.endDate) < new Date(updated.startDate)) {
        updated.endDate = ''
      }
      return updated
    })
    setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const handleSubmit = () => {
    const newErrors: DateRangeProps = {
      startDate: selectedDate.startDate ? '' : 'From Date is required',
      endDate: selectedDate.endDate ? '' : 'To Date is required'
    }
    setErrors(newErrors)
    if (newErrors.startDate || newErrors.endDate) {
      return
    }
    setLockedDateRange(selectedDate)
    lockIncentiveDateRange({ fromDate: selectedDate.startDate, toDate: selectedDate.endDate }).then((response: any) => {
      if (response?.status === 200) {
        snackbarService.next({ data: { open: true, severity: 'success', message: 'Date range locked successfully' } })
      }
    })
  }

  const handleReset = () => {
    setSelectedDate(initialDateRange)
    setLockedDateRange(initialDateRange)
    setErrors(initialDateRange)
  }

  const handleCalculate = () => {
    const dateRange = lockedDateRange.startDate && lockedDateRange.endDate ? lockedDateRange : selectedDate
    if (!dateRange.startDate || !dateRange.endDate) {
      snackbarService.next({ data: { open: true, severity: 'error', message: 'Please select calculate date range' } })
      return
    }
    calculateIncentive({ fromDate: dateRange.startDate, toDate: dateRange.endDate }).then((response: any) => {
      if (response?.status === 200) {
        snackbarService.next({ data: { open: true, severity: 'success', message: 'Incentive calculated successfully' } })
        fetchIncentive()
      }
    })
  }

  const handleSaveEditedData = () => {
    const payload = Object.values(editedRows)
    if (payload.length === 0) {
      snackbarService.next({ data: { open: true, severity: 'error', message: 'No changes to save' } })
      return
    }
    swal({
      title: 'Are you sure?',
      text: 'Do you want to save the edited operator count data?',
      icon: 'warning',
      buttons: true as unknown as ButtonList | (string | boolean)[] | undefined,
    }).then((confirmed) => {
      if (!confirmed) {
        return
      }
      editBPCount(payload).then((response: any) => {
        if (response?.status === 200) {
          snackbarService.next({ data: { open: true, severity: 'success', message: 'Data saved successfully' } })
          setEditedRows({})
          fetchIncentive()
        }
      })
    })
  }

  const handleDownloadExcel = () => {
    if (rowData.length === 0) {
      snackbarService.next({ data: { open: true, severity: 'error', message: 'Data not available' } })
      return
    }
    const header = ['User Id', 'Name', 'Operator Count', 'Original Count', 'Incentive Amount']
    const data = rowData.map((row) => ([
      row.employeeId,
      row.employeeName,
      row.count,
      row.originalCount,
      row.incentive !== undefined && row.incentive !== null ? `₹ ${row.incentive.toLocaleString('en-IN')}` : ''
    ]))
    const ws = utils.aoa_to_sheet([header, ...data])

    header.forEach((_, colIndex) => {
      const cellRef = utils.encode_cell({ r: 0, c: colIndex })
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true }
        }
      }
    })

    ws['!cols'] = header.map((headerText, colIndex) => {
      const maxDataLength = data.reduce((max, row) => {
        const value = row[colIndex]
        const length = value !== undefined && value !== null ? String(value).length : 0
        return Math.max(max, length)
      }, headerText.length)
      return { wch: maxDataLength + 2 }
    })

    const wb = utils.book_new()
    utils.book_append_sheet(wb, ws, 'Boarding Pass Count')
    writeFile(wb, 'Boarding Pass Count.xlsx')
  }

  return (
    <div className='main'>
      <div className='main-container'>
        <div className='heading heading-center-space-between height-40'>
          <h2>Boarding Pass Incentive Calculation</h2>
        </div>
        <div className='cal-40'>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className='bp-count-date-range-container'>
              {
                dateRangeField.map((item, index: number) => (
                  <div key={index} className='bp-count-date-sub-container'>
                    <CustomDatePicker
                      label={item.label}
                      name={item.name}
                      selectedDate={selectedDate}
                      handleDate={handleDate}
                      minDate={item.name === 'endDate' ? selectedDate.startDate : undefined}
                    />
                    {
                      errors[item.name as keyof DateRangeProps] &&
                      <span className='bp-count-error-text'>{errors[item.name as keyof DateRangeProps]}</span>
                    }
                  </div>
                ))
              }
              <div className='bp-count-button-group'>
                <Button
                  variant='contained'
                  className='bp-count-submit-button'
                  onClick={handleSubmit}
                >
                  Lock
                </Button>
                <Button
                  variant='outlined'
                  className='bp-count-reset-button'
                  onClick={handleReset}
                >
                  Reset
                </Button>
              </div>
            </div>
            <div className='bp-count-admin-table-header'>
              <Button
                variant='contained'
                className='bp-count-calculate-button'
                onClick={handleCalculate}
              >
                Calculate
              </Button>
              <Button
                variant='outlined'
                className='bp-count-download-button'
                startIcon={<DownloadIcon />}
                onClick={handleDownloadExcel}
              >
                Download Excel
              </Button>
              <Button
                variant='contained'
                className='bp-count-save-button'
                onClick={handleSaveEditedData}
              >
                Save Edited Data
              </Button>
            </div>
            <div className='ag-grid-container' style={{ flex: 1 }}>
              <AgGridContainer
                rowData={rowData}
                columnDefs={columnDefs}
                editType='fullRow'
                stopEditingWhenCellsLoseFocus={true}
                singleClickEdit={true}
                onCellValueChanged={onCellValueChanged}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BPCount
